import React, { useState, useEffect, useCallback, useRef } from 'react';
import Modal from './Modal';
import { API_CONFIG, StudentOptionsResponse } from '../config/api';

const COURSE_DROPDOWN_MAX_ITEMS = 5;

export interface AssignGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId?: string;
  consultantDisplayName?: string;
  consultantMeta?: string;
  onSaved?: () => void;
}

interface RuleItem {
  id: string;
  year: string;
  course: string;
}

const AssignGroupModal: React.FC<AssignGroupModalProps> = ({
  isOpen,
  onClose,
  userId,
  consultantDisplayName,
  consultantMeta,
  onSaved
}) => {
  const [courses, setCourses] = useState<string[]>([]);
  const [yearByCourse, setYearByCourse] = useState<Record<string, string[]>>({});
  const [selectedCourse, setSelectedCourse] = useState<string>('');
  const [selectedYear, setSelectedYear] = useState<string>('');
  const [rules, setRules] = useState<RuleItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [courseDropdownOpen, setCourseDropdownOpen] = useState(false);
  const [courseSearchQuery, setCourseSearchQuery] = useState('');
  const courseDropdownRef = useRef<HTMLDivElement>(null);

  const fetchStudentOptions = useCallback(async () => {
    setLoading(true);
    setOptionsError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(API_CONFIG.ENDPOINTS.USERS_ADMIN_STUDENT_OPTIONS, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to load options');
      const json = await response.json();
      const data = json.data as StudentOptionsResponse;
      setCourses(data.courses || []);
      setYearByCourse(data.yearByCourse || {});
      setSelectedCourse('');
      setSelectedYear('');
    } catch (err) {
      setOptionsError(err instanceof Error ? err.message : 'Failed to load year and course options');
      setCourses([]);
      setYearByCourse({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchStudentOptions();
    }
  }, [isOpen, fetchStudentOptions]);

  const fetchAssignments = useCallback(async () => {
    if (!userId) return;
    setAssignmentsLoading(true);
    setSaveError(null);
    try {
      const token = localStorage.getItem('token');
      const url = `${API_CONFIG.ENDPOINTS.USERS_ADMIN_CONSULTANT_ASSIGNMENTS}/${userId}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to load assignments');
      const json = await response.json();
      const data = json.data || {};
      const ruleList = Array.isArray(data.rules) ? data.rules : [];
      setRules(
        ruleList.map((r: { course?: string; yearLevel?: string }, i: number) => ({
          id: `rule-${i}-${Date.now()}`,
          course: (r.course != null ? String(r.course) : '').trim(),
          year: (r.yearLevel != null ? String(r.yearLevel) : '').trim()
        }))
      );
    } catch {
      setRules([]);
    } finally {
      setAssignmentsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (isOpen && userId) {
      fetchAssignments();
    }
  }, [isOpen, userId, fetchAssignments]);

  const yearsForSelectedCourse = selectedCourse ? (yearByCourse[selectedCourse] || []) : [];

  const handleCourseChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newCourse = e.target.value;
    setSelectedCourse(newCourse);
    const years = newCourse ? (yearByCourse[newCourse] || []) : [];
    if (selectedYear && years.length > 0 && !years.includes(selectedYear)) {
      setSelectedYear(years[years.length - 1]);
    } else if (years.length === 0) {
      setSelectedYear('');
    } else if (years.length > 0 && !years.includes(selectedYear)) {
      setSelectedYear(years[years.length - 1]);
    }
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedYear(e.target.value);
  };

  const handleAddRule = () => {
    if (!selectedCourse || !selectedYear) return;
    setRules((prev) => [
      ...prev,
      { id: `rule-${Date.now()}`, course: selectedCourse, year: selectedYear }
    ]);
  };

  const handleRemoveRule = (id: string) => {
    setRules((prev) => prev.filter((r) => r.id !== id));
  };

  const handleSave = async () => {
    if (!userId) {
      onClose();
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const token = localStorage.getItem('token');
      const url = `${API_CONFIG.ENDPOINTS.USERS_ADMIN_CONSULTANT_ASSIGNMENTS}/${userId}`;
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          rules: rules.map((r) => ({ course: r.course, yearLevel: r.year }))
        })
      });
      const json = await response.json();
      if (!response.ok) {
        throw new Error(json.message || 'Failed to save assignments');
      }
      onSaved?.();
      onClose();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save assignments');
    } finally {
      setSaving(false);
    }
  };

  const hasOptions = courses.length > 0;

  const filteredCourses = courseSearchQuery.trim()
    ? courses.filter((c) =>
        c.toLowerCase().includes(courseSearchQuery.trim().toLowerCase())
      )
    : courses;
  const displayedCourses = filteredCourses.slice(0, COURSE_DROPDOWN_MAX_ITEMS);

  useEffect(() => {
    if (!courseDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        courseDropdownRef.current &&
        !courseDropdownRef.current.contains(e.target as Node)
      ) {
        setCourseDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [courseDropdownOpen]);

  return (
    <Modal
      isOpen={isOpen}
      title="Assignment Builder"
      onClose={onClose}
      className="assign-group-modal"
    >
      <div className="assign-group-modal__body">
        {/* Section 1: Consultant (read-only when userId provided) */}
        {userId && (
          <div className="assign-group-modal__section">
            <h3 className="assign-group-modal__section-title">Consultant</h3>
            <div className="assign-group-modal__selected">
              <div className="assign-group-modal__selected-avatar">
                {(consultantDisplayName || '')
                  .trim()
                  .split(/\s+/)
                  .map((n) => n[0])
                  .join('')
                  .toUpperCase()
                  .slice(0, 2) || '—'}
              </div>
              <div className="assign-group-modal__selected-info">
                <p className="assign-group-modal__selected-name">{consultantDisplayName || 'Consultant'}</p>
                <p className="assign-group-modal__selected-meta">{consultantMeta || `ID: ${userId}`}</p>
              </div>
            </div>
          </div>
        )}
        {saveError && (
          <div className="assign-group-modal__section" role="alert">
            <p className="assign-group-modal__error">{saveError}</p>
          </div>
        )}

        {/* Section 2: Define Access Rules (course first, then year) */}
        <div className="assign-group-modal__section">
          <h3 className="assign-group-modal__section-title">Define Access Rules</h3>
          {assignmentsLoading && rules.length === 0 ? (
            <p className="assign-group-modal__loading">Loading current permissions…</p>
          ) : loading ? (
            <p className="assign-group-modal__loading">Loading year and course options…</p>
          ) : optionsError ? (
            <p className="assign-group-modal__error" role="alert">{optionsError}</p>
          ) : !hasOptions ? (
            <p className="assign-group-modal__empty">Import students to define year and course options.</p>
          ) : (
            <div className="assign-group-modal__rule-box">
              <div className="assign-group-modal__rule-grid">
                <div className="assign-group-modal__rule-field" ref={courseDropdownRef}>
                  <label className="form-control__label assign-group-modal__label">Course</label>
                  <div className="assign-group-modal__course-dropdown">
                    <button
                      type="button"
                      className="assign-group-modal__course-trigger form-select assign-group-modal__select"
                      aria-label="Course"
                      aria-expanded={courseDropdownOpen}
                      aria-haspopup="listbox"
                      onClick={() => setCourseDropdownOpen((prev) => !prev)}
                    >
                      <span className="assign-group-modal__course-trigger-text">
                        {selectedCourse || 'Select Course'}
                      </span>
                      <span className="assign-group-modal__course-trigger-icon material-symbols-outlined" aria-hidden>
                        {courseDropdownOpen ? 'expand_less' : 'expand_more'}
                      </span>
                    </button>
                    {courseDropdownOpen && (
                      <div
                        className="assign-group-modal__course-panel"
                        role="listbox"
                        aria-label="Course options"
                      >
                        <div className="assign-group-modal__course-search-wrap">
                          <span className="assign-group-modal__course-search-icon material-symbols-outlined" aria-hidden>search</span>
                          <input
                            type="text"
                            className="form-input assign-group-modal__course-search-input"
                            placeholder="Search course..."
                            aria-label="Search course"
                            value={courseSearchQuery}
                            onChange={(e) => setCourseSearchQuery(e.target.value)}
                            onKeyDown={(e) => e.stopPropagation()}
                          />
                        </div>
                        <ul className="assign-group-modal__course-list">
                          {displayedCourses.length === 0 ? (
                            <li className="assign-group-modal__course-empty">No courses match</li>
                          ) : (
                            displayedCourses.map((c) => (
                              <li
                                key={c}
                                className="assign-group-modal__course-option"
                                role="option"
                                aria-selected={selectedCourse === c}
                                onClick={() => {
                                  setSelectedCourse(c);
                                  setCourseDropdownOpen(false);
                                  setCourseSearchQuery('');
                                  const years = yearByCourse[c] || [];
                                  if (years.length > 0 && (!selectedYear || !years.includes(selectedYear))) {
                                    setSelectedYear(years[years.length - 1]);
                                  } else if (years.length === 0) {
                                    setSelectedYear('');
                                  }
                                }}
                              >
                                {c}
                              </li>
                            ))
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
                <div className="assign-group-modal__rule-field">
                  <label className="form-control__label assign-group-modal__label">Year Level</label>
                  <select
                    className="form-select assign-group-modal__select"
                    aria-label="Year level"
                    value={selectedYear}
                    onChange={handleYearChange}
                    disabled={!selectedCourse}
                  >
                    <option value="">{selectedCourse ? 'Select Year Level' : 'Select course first'}</option>
                    {yearsForSelectedCourse.map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
                <div className="assign-group-modal__rule-action">
                  <button
                    type="button"
                    className="btn btn-primary assign-group-modal__add-btn"
                    onClick={handleAddRule}
                    disabled={!selectedCourse || !selectedYear}
                  >
                    <span className="material-symbols-outlined">add</span>
                    <span className="assign-group-modal__add-label">Add Rule</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Section 3: Current Access Permissions */}
        <div className="assign-group-modal__section">
          <div className="assign-group-modal__rules-header">
            <h3 className="assign-group-modal__section-title">Current Access Permissions</h3>
            <span className="assign-group-modal__badge">{rules.length} Active</span>
          </div>
          <ul className="assign-group-modal__rule-list">
            {rules.length === 0 ? (
              <li className="assign-group-modal__rule-item assign-group-modal__rule-item--empty">
                <span className="assign-group-modal__rule-empty-text">No rules added yet. Select a course and year above, then click Add Rule.</span>
              </li>
            ) : (
              rules.map((rule, index) => {
                const iconClass = ['assign-group-modal__rule-icon--blue', 'assign-group-modal__rule-icon--purple', 'assign-group-modal__rule-icon--emerald'][index % 3];
                return (
                  <li key={rule.id} className="assign-group-modal__rule-item">
                    <div className="assign-group-modal__rule-item-content">
                      <div className={`assign-group-modal__rule-icon ${iconClass}`}>
                        <span className="material-symbols-outlined">school</span>
                      </div>
                      <div className="assign-group-modal__rule-text">
                        <span className="assign-group-modal__rule-year">{rule.year}</span>
                        <span className="assign-group-modal__rule-sep">•</span>
                        <span className="assign-group-modal__rule-course">{rule.course}</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="assign-group-modal__rule-remove"
                      title="Remove Rule"
                      aria-label="Remove rule"
                      onClick={() => handleRemoveRule(rule.id)}
                    >
                      <span className="material-symbols-outlined">delete</span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      </div>

      <div className="assign-group-modal__footer">
        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={userId ? handleSave : onClose}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </div>
    </Modal>
  );
};

export default AssignGroupModal;
